/**
 * Voting Page
 *
 * Story FOS-4.2.7: VotingPage Implementation
 * Task 9: Refactor VotingPage to use new components
 *
 * Complete voting interface with real-time updates, vote confirmation, and quorum tracking.
 * Integrates all voting components created in Tasks 2-8.
 *
 * @see AC-4.2.7.1 - VoteConfirmationModal shows vote choice and proposal title
 * @see AC-4.2.7.2 - Success shows vote-pass NFT ID
 * @see AC-4.2.7.3 - NeedsRefinement requires feedback
 * @see AC-4.2.7.4 - QuorumProgressBar shows absolute counts
 * @see AC-4.2.7.5 - Timer shows urgency colors
 * @see AC-4.2.7.6 - VoteBreakdownChart updates with new votes
 *
 * @module pages/Governance/VotingPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Vote } from 'lucide-react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import {
  VotingHeader,
  ProposalSummary,
  VoteSelector,
  FeedbackInput,
  QuorumProgressBar,
  VoteBreakdownChart,
  VoteConfirmationModal,
} from '../../components/governance';
import {
  castVote,
  getProposalDetails,
  getVoteBreakdown,
  isGovernanceError,
  GovernanceErrorType,
  type VoteChoice,
  type ProposalDetails,
  type VoteBreakdown,
} from '../../services/governanceService';

// =============================================================================
// Types
// =============================================================================

interface VotingState {
  selectedVote: VoteChoice | null;
  feedback: string;
  isConfirmModalOpen: boolean;
  hasVoted: boolean;
  voteNftId?: number;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Voting page with full voting interface
 */
export function VotingPage() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();

  // Data state
  const [proposal, setProposal] = useState<ProposalDetails | null>(null);
  const [breakdown, setBreakdown] = useState<VoteBreakdown | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Voting state
  const [votingState, setVotingState] = useState<VotingState>({
    selectedVote: null,
    feedback: '',
    isConfirmModalOpen: false,
    hasVoted: false,
  });

  // Parse proposal ID
  const parsedProposalId = proposalId ? parseInt(proposalId, 10) : null;

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const loadData = useCallback(async () => {
    if (!parsedProposalId || isNaN(parsedProposalId)) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [proposalData, breakdownData] = await Promise.all([
        getProposalDetails(parsedProposalId),
        getVoteBreakdown(parsedProposalId),
      ]);

      setProposal(proposalData);
      setBreakdown(breakdownData);
    } catch (e) {
      // Check if it's a real governance error (not network)
      if (isGovernanceError(e) && e.type !== GovernanceErrorType.NETWORK_ERROR) {
        if (e.type === GovernanceErrorType.NOT_FOUND) {
          setError('Proposal not found');
        } else {
          setError(e.message);
        }
        setIsLoading(false);
        return;
      }

      // No data available when API is not reachable
      setError('Proposal not found');
      setIsLoading(false);
      return;
    } finally {
      setIsLoading(false);
    }
  }, [parsedProposalId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==========================================================================
  // Vote Handling
  // ==========================================================================

  const handleVoteSelect = (choice: VoteChoice) => {
    setVotingState((prev) => ({
      ...prev,
      selectedVote: choice,
      // Clear feedback if not NeedsRefinement
      feedback: choice !== 'NeedsRefinement' ? '' : prev.feedback,
    }));
  };

  const handleFeedbackChange = (value: string) => {
    setVotingState((prev) => ({ ...prev, feedback: value }));
  };

  const handleOpenConfirmModal = () => {
    setVotingState((prev) => ({ ...prev, isConfirmModalOpen: true }));
  };

  const handleCloseConfirmModal = () => {
    setVotingState((prev) => ({ ...prev, isConfirmModalOpen: false }));
  };

  const handleConfirmVote = async () => {
    if (!proposal || !votingState.selectedVote) {
      return { success: false, error: 'No vote selected' };
    }

    try {
      const response = await castVote({
        proposal_id: proposal.id,
        choice: votingState.selectedVote,
        refinement_feedback:
          votingState.selectedVote === 'NeedsRefinement' ? votingState.feedback : undefined,
      });

      if (response.success) {
        // Update local state
        setVotingState((prev) => ({
          ...prev,
          hasVoted: true,
          voteNftId: response.vote_pass_nft_id,
        }));

        // Refresh breakdown to show updated vote count
        if (parsedProposalId && !isNaN(parsedProposalId)) {
          const newBreakdown = await getVoteBreakdown(parsedProposalId);
          setBreakdown(newBreakdown);
        }
      }

      return response;
    } catch (e) {
      if (isGovernanceError(e)) {
        return { success: false, error: e.message };
      }
      return { success: false, error: 'Failed to submit vote' };
    }
  };

  // ==========================================================================
  // Derived State
  // ==========================================================================

  const canSubmitVote =
    votingState.selectedVote !== null &&
    !votingState.hasVoted &&
    (votingState.selectedVote !== 'NeedsRefinement' || votingState.feedback.trim().length > 0);

  const isVotingEnded = proposal ? proposal.voting_ends_at <= Date.now() : false;

  // ==========================================================================
  // Render
  // ==========================================================================

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="voting-page-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Empty state - no proposal selected (normal navigation, not an error)
  if (!parsedProposalId || isNaN(parsedProposalId)) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="voting-page-empty">
        <Vote className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No proposal selected</h3>
        <p className="text-muted-foreground mb-4">
          Select a proposal from the proposals list to cast your vote.
        </p>
        <button
          onClick={() => navigate('/proposals')}
          className="text-sm text-primary hover:underline"
        >
          Browse Proposals
        </button>
      </div>
    );
  }

  // Error state
  if (error || !proposal || !breakdown) {
    return (
      <div className="py-8" data-testid="voting-page-error">
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">
              {error || 'Failed to load proposal'}
            </p>
            <button
              onClick={() => navigate('/proposals')}
              className="text-sm text-red-600 dark:text-red-400 hover:underline mt-1"
            >
              Return to proposals
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div data-testid="voting-page">
        {/* Header with back link and countdown */}
        <VotingHeader
          title={proposal.title}
          proposalId={proposal.id}
          votingEndsAt={proposal.voting_ends_at}
        />

        {/* Proposal summary */}
        <div className="mb-6">
          <ProposalSummary
            id={proposal.id}
            title={proposal.title}
            category={proposal.category}
            summary={proposal.summary}
            proposerName={proposal.proposer_name}
            createdAt={proposal.created_at}
          />
        </div>

        {/* Vote breakdown */}
        <div className="mb-6 p-4 bg-card rounded-lg border border-border">
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Current Vote Results</h3>
          <VoteBreakdownChart
            yes={breakdown.yes}
            no={breakdown.no}
            abstain={breakdown.abstain}
            needsRefinement={breakdown.needs_refinement}
          />
          <div className="mt-4">
            <QuorumProgressBar
              currentVotes={breakdown.total_votes}
              requiredVotes={breakdown.quorum_threshold}
            />
          </div>
        </div>

        {/* Voting interface */}
        {votingState.hasVoted ? (
          // Already voted state
          <div
            className="p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
            data-testid="voting-page-voted"
          >
            <h3 className="font-medium text-green-700 dark:text-green-300 mb-2">
              Your Vote Has Been Recorded
            </h3>
            <p className="text-sm text-green-600 dark:text-green-400">
              You voted: <strong>{votingState.selectedVote}</strong>
            </p>
            {votingState.voteNftId && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                Vote-pass NFT: <strong>#{votingState.voteNftId}</strong>
              </p>
            )}
          </div>
        ) : isVotingEnded ? (
          // Voting ended state
          <div
            className="p-6 bg-muted/50 rounded-lg border border-border"
            data-testid="voting-page-ended"
          >
            <h3 className="font-medium text-foreground mb-2">Voting Has Ended</h3>
            <p className="text-sm text-muted-foreground">
              The voting period for this proposal has closed.
            </p>
          </div>
        ) : (
          // Active voting state
          <div className="space-y-6">
            <div className="p-6 bg-card rounded-lg border border-border">
              <h3 className="font-medium text-foreground mb-4">Cast Your Vote</h3>

              {/* Vote options */}
              <VoteSelector
                selected={votingState.selectedVote}
                onChange={handleVoteSelect}
                disabled={votingState.hasVoted}
              />

              {/* Feedback input for NeedsRefinement */}
              {votingState.selectedVote === 'NeedsRefinement' && (
                <div className="mt-4">
                  <FeedbackInput value={votingState.feedback} onChange={handleFeedbackChange} />
                </div>
              )}

              {/* Submit button */}
              <div className="mt-6">
                <button
                  onClick={handleOpenConfirmModal}
                  disabled={!canSubmitVote}
                  className="w-full sm:w-auto px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="voting-page-submit"
                >
                  Cast Vote
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation modal */}
        <VoteConfirmationModal
          isOpen={votingState.isConfirmModalOpen}
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmVote}
          proposalId={proposal.id}
          proposalTitle={proposal.title}
          voteChoice={votingState.selectedVote || 'Yes'}
          feedback={votingState.feedback}
        />
      </div>
    </ErrorBoundary>
  );
}

export default VotingPage;
